/**
 * ConceptoPrimerUsoModal — confirmación/edición de parámetros estimados (spec §5.6)
 *
 * La primera vez que se usa un concepto GLOBAL o MIGRADO (propio, origen
 * 'migracion') con parámetros estimados (unidad de carga con kg por unidad
 * y/o valores nutricionales PB/EM), el formulario muestra este modal antes
 * de continuar:
 *
 * - **Confirmar** → registra la fila en `conceptos_confirmados` y no vuelve a
 *   preguntar para esa empresa; se sigue usando el mismo concepto.
 * - **Editar valores** → para un GLOBAL crea la versión propia (`crearMiVersion`,
 *   spec §3.8) con los valores corregidos; para un MIGRADO (concepto propio)
 *   hace `update` directo sobre el mismo registro.
 * - **Cancelar** → se usa el concepto sin confirmar y se vuelve a preguntar la
 *   próxima vez (nunca bloquea el flujo).
 *
 * Variante **completar nutrición** (P3a): cuando el form marca
 * `pedirNutricionFaltante` (alimento de categoría alimentacion_engorde con
 * PB/EM faltantes y sin unidad de carga), no hay valores estimados que
 * confirmar → el modal pide directamente completar PB/EM. Confirmar sin
 * completar también registra el concepto (el badge de "sin nutrición" en la
 * página de Conceptos queda como recordatorio).
 */
import React, { useState } from 'react';
import { getRepositories } from '../../services/storage';
import { Concepto } from '../../types/administracion';

export interface ConceptoPrimerUsoModalProps {
  concepto: Concepto;
  empresaId: string;
  /** El concepto es un alimento con PB/EM faltantes → pedir completar la nutrición en vez de confirmar estimados */
  pedirNutricionFaltante?: boolean;
  /** Confirmó los valores → sigue con el mismo concepto (ya registrado en conceptos_confirmados) */
  onConfirmado: () => void;
  /** Editó valores → global: concepto devuelto por crearMiVersion; propio (migrado): el mismo concepto con los cambios aplicados */
  onVersionCreada: (concepto: Concepto) => void;
  /** Canceló → usa el concepto sin confirmar; se volverá a preguntar */
  onCancelar: () => void;
}

/** "rollos" → "rollo", "unidades" → "unidad" (kg queda kg) */
const singular = (unidad: string): string => {
  if (unidad === 'unidades') return 'unidad';
  if (unidad.endsWith('s') && unidad !== 'kg') return unidad.slice(0, -1);
  return unidad;
};

const numeroAr = (n: number, decimales = 1): string =>
  n.toLocaleString('es-AR', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

const inputClassName =
  'w-full rounded-md border border-hairline bg-white px-3 py-2 text-ink placeholder:text-sage/70 focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/20';

export const ConceptoPrimerUsoModal: React.FC<ConceptoPrimerUsoModalProps> = ({
  concepto,
  empresaId,
  pedirNutricionFaltante = false,
  onConfirmado,
  onVersionCreada,
  onCancelar,
}) => {
  const tieneCarga = !!concepto.unidad_carga && concepto.unidad_carga !== 'kg';
  // Solo cuenta como estimado si PB y EM están completos (datos migrados pueden
  // traer la columna con campos en null → no hay valores que mostrar)
  const tieneNutricion =
    !!concepto.nutricion && concepto.nutricion.pb != null && concepto.nutricion.em != null;
  // Alimento sin unidad de carga y sin nutrición → pedir directamente PB/EM
  const modoCompletar = pedirNutricionFaltante && !tieneCarga;
  const mostrarNutricion = tieneNutricion || pedirNutricionFaltante;

  const [editando, setEditando] = useState(modoCompletar);
  const [kgPorUnidad, setKgPorUnidad] = useState(
    concepto.kg_por_unidad != null ? String(concepto.kg_por_unidad) : ''
  );
  const [pb, setPb] = useState(
    concepto.nutricion?.pb != null ? String(concepto.nutricion.pb) : ''
  );
  const [em, setEm] = useState(
    concepto.nutricion?.em != null ? String(concepto.nutricion.em) : ''
  );
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const parseNumero = (s: string): number | null => {
    const n = parseFloat(s.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  };

  const handleConfirmar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const { concepto: conceptoRepo } = getRepositories();
      await conceptoRepo.confirmar(concepto.id, empresaId);
      onConfirmado();
    } catch (e) {
      console.error('ConceptoPrimerUsoModal: error confirmando concepto', e);
      setError('No se pudo confirmar el concepto. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarVersion = async () => {
    const cambios: Partial<Concepto> = {};
    if (tieneCarga) {
      const kg = parseNumero(kgPorUnidad);
      if (kg === null || kg <= 0) {
        setError(`El kg por ${singular(concepto.unidad_carga!)} debe ser mayor a 0`);
        return;
      }
      cambios.kg_por_unidad = kg;
    }
    if (mostrarNutricion) {
      const pbNum = parseNumero(pb);
      const emNum = parseNumero(em);
      if (pbNum !== null && emNum !== null) {
        cambios.nutricion = { pb: pbNum, em: emNum };
      } else if (modoCompletar) {
        // Acá el objetivo del modal ES la nutrición: sin ambos valores no hay
        // nada que guardar (el camino sin valores es Confirmar)
        setError('Completá PB y EM para guardar los valores, o confirmá sin completar.');
        return;
      } else if (pedirNutricionFaltante && (pbNum !== null || emNum !== null)) {
        // Alimento con carga: no se guarda nutrición a medias
        setError('Completá PB y EM para guardar los valores nutricionales.');
        return;
      }
    }

    setGuardando(true);
    setError(null);
    try {
      const { concepto: conceptoRepo } = getRepositories();
      if (concepto.empresa_id === null) {
        // Global → crea la versión propia (comportamiento P1).
        // Sin empresa_activa_id en el perfil, crearMiVersion caería a empresa_id ''
        // (RLS la rechaza en sync → pending en retry perpetuo): se inyecta la
        // empresa que el form ya resolvió (misma guardia que el picker).
        const propio = await conceptoRepo.crearMiVersion(concepto.id, {
          ...cambios,
          empresa_id: empresaId,
        });
        onVersionCreada(propio);
      } else {
        // Propio (migrado o creado por la empresa) → update directo, sin copia.
        // El update fija los valores pero NO confirma el concepto: sin el
        // confirmar de abajo, este modal reaparecería en cada uso del migrado
        // (spec §5.6: el paso extra es SOLO la primera vez).
        await conceptoRepo.update({ id: concepto.id, ...cambios });
        await conceptoRepo.confirmar(concepto.id, empresaId);
        onVersionCreada({ ...concepto, ...cambios });
      }
    } catch (e) {
      console.error('ConceptoPrimerUsoModal: error guardando valores del concepto', e);
      setError(
        concepto.empresa_id === null
          ? 'No se pudo crear tu versión del concepto. Probá de nuevo.'
          : 'No se pudo guardar el concepto. Probá de nuevo.'
      );
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={modoCompletar ? 'Completar valores del concepto' : 'Confirmar valores del concepto'}
        data-ar-guide="modal-nutricion"
        className="w-full max-w-md rounded-xl bg-paper shadow-overlay p-6 space-y-4"
      >
        <div>
          <h3 className="text-lg font-bold text-ink">
            {modoCompletar
              ? `Completá los valores de «${concepto.nombre}»`
              : 'Confirmar valores del concepto'}
          </h3>
          {modoCompletar ? (
            <p className="text-sm text-sage">
              Los valores están en la etiqueta de la bolsa. Si no los tenés a mano, confirmá sin
              completar y los podés editar después desde Conceptos.
            </p>
          ) : (
            <p className="text-sm text-sage">
              “{concepto.nombre}” es un concepto sugerido con valores estimados. Revisalos antes de
              usarlo.
            </p>
          )}
        </div>

        {/* Valores estimados del global */}
        {!modoCompletar && (
          <div className="rounded-md border border-hairline bg-paper-2/40 p-3 space-y-1 text-sm text-ink">
            <p>
              Unidad base: <strong>{concepto.unidad}</strong>
            </p>
            {tieneCarga && (
              <p data-testid="modal-carga">
                1 {singular(concepto.unidad_carga!)} = {numeroAr(concepto.kg_por_unidad ?? 0, 0)} kg
              </p>
            )}
            {tieneNutricion && (
              <p data-testid="modal-nutricion">
                PB: {numeroAr(concepto.nutricion!.pb)} % · EM:{' '}
                {numeroAr(concepto.nutricion!.em, 2)} Mcal/kg
              </p>
            )}
          </div>
        )}

        {editando && (
          <div className="space-y-3">
            {tieneCarga && (
              <label className="block text-sm font-medium text-ink">
                kg por {singular(concepto.unidad_carga!)}
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className={`mt-1 ${inputClassName}`}
                  value={kgPorUnidad}
                  onChange={e => setKgPorUnidad(e.target.value)}
                />
              </label>
            )}
            {mostrarNutricion && (
              <div className="grid grid-cols-2 gap-2">
                <label data-ar-guide="modal-nutricion-pb" className="block text-sm font-medium text-ink">
                  PB (%)
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    className={`mt-1 ${inputClassName}`}
                    value={pb}
                    onChange={e => setPb(e.target.value)}
                  />
                </label>
                <label data-ar-guide="modal-nutricion-em" className="block text-sm font-medium text-ink">
                  EM (Mcal/kg)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`mt-1 ${inputClassName}`}
                    value={em}
                    onChange={e => setEm(e.target.value)}
                  />
                </label>
              </div>
            )}
            <p className="text-xs text-sage">
              {concepto.empresa_id === null
                ? 'Al guardar se crea tu versión del concepto (la original queda intacta para el resto).'
                : 'Al guardar se actualizan los valores de este concepto.'}
            </p>
          </div>
        )}

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className="rounded-md border border-hairline px-3 py-2 text-sm text-ink hover:bg-hairline/50 disabled:opacity-50"
          >
            Cancelar
          </button>
          {editando ? (
            <>
              {/* Modo completar: confirmar sin completar sigue disponible (el
                  badge de "sin nutrición" en Conceptos queda de recordatorio) */}
              {modoCompletar && (
                <button
                  type="button"
                  onClick={handleConfirmar}
                  disabled={guardando}
                  className="rounded-md border border-forest/40 px-3 py-2 text-sm text-forest hover:bg-forest/10 disabled:opacity-50"
                >
                  Confirmar
                </button>
              )}
              <button
                type="button"
                onClick={handleGuardarVersion}
                disabled={guardando}
                className="rounded-md bg-forest px-4 py-2 text-sm font-medium text-white hover:bg-forest/90 disabled:opacity-50"
              >
                {modoCompletar
                  ? 'Guardar valores'
                  : concepto.empresa_id === null
                  ? 'Guardar mi versión'
                  : 'Guardar cambios'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setEditando(true)}
                disabled={guardando}
                className="rounded-md border border-forest/40 px-3 py-2 text-sm text-forest hover:bg-forest/10 disabled:opacity-50"
              >
                Editar valores
              </button>
              <button
                type="button"
                onClick={handleConfirmar}
                disabled={guardando}
                data-ar-guide="modal-nutricion-confirmar"
                className="rounded-md bg-forest px-4 py-2 text-sm font-medium text-white hover:bg-forest/90 disabled:opacity-50"
              >
                Confirmar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
